import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ClockIcon } from '../../../../assets/icons/clock-icon.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-circle.svg';
import { ReactComponent as PaidAmountIcon } from '../../../../assets/icons/current-price-sae.svg';
import { ReactComponent as ProfileIcon } from '../../../../assets/icons/profile.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  GuestChargingStationInterFace,
  RequestGuestChargingStationInterFace,
} from '../../../../interface/guest-charging-station-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  disableGuestChargingStationFiltersApplied,
  fetchGuestChargingStation,
  fetchListGuestChargingStationFilters,
  updateGuestChargingStationFilters,
  setSortData,
} from '../../../../redux/slices/GuestChargingStationSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DisableSwitchWarning from '../../../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../../shared-components/switch-button-field/SwitchButton';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../shared-functions/FormatUtcString';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import stationManagementApi from '../../../../utils/api/station-management';

import GuestChargingChart from './GuestChargingChart';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
  closeModal: () => void;
}

/**
 * Guest charging component
 * @returns
 */
function GuestCharging() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
    /**
     * Close modal
     */
    closeModal: () => setModalBoxStatus('close'),
  });
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const {
    GuestChargingStation,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.guestChargingStation);
  const [selectedStation, setSelectedStation] = useState<GuestChargingStationInterFace | null>(
    null,
  );
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);

  /**
   * Handle the fetch the data in api function
   * @param {RequestGuestChargingStationInterFace} data The request data
   */
  const fetchData = (data: RequestGuestChargingStationInterFace) => {
    if (!loading && selectedChargingPoint) {
      dispatch(fetchGuestChargingStation({ ...data, chargingStation: selectedChargingPoint.id }));
    }
  };
  /**
   * Confirm the status change function
   * @param {string} id The device id
   * @param {boolean} checked The new status
   */
  const confirmStatusChange = (id: string, checked: boolean) => {
    stationManagementApi
      .updateGuestStatus(id, { isActive: checked })
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          content: (
            <SuccessNotification
              title={t('statusChangedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                fetchData({ page: DefaultApiLimit.page, limit });
                // dispatch(updateSingleStation(response.data));
              }}
            />
          ),
          notificationModal: true,
          header: false,
          /**
           * Close modal with function to dispatch station details
           */
          closeModal: () => {
            setModalBoxStatus('close');
            // dispatch(updateSingleStation(response.data));
          },
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when disabling a device
   * @param {AvailableDevice} device - The device to update
   * @param {boolean} checked - The new availability status
   */
  const handleAvailabilityChange = (device: GuestChargingStationInterFace, checked: boolean) => {
    if (checked) {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: t('disableDeviceWarning'),
        content: (
          <DisableSwitchWarning
            selectedId={device._id}
            onCancel={() => setModalBoxStatus('close')}
            message={t('guestChargingWarningMessage')}
            onConfirm={() => confirmStatusChange(device._id, checked)}
          />
        ),
        notificationModal: true,
        header: false,
        /**
         * Close modal
         */
        closeModal: () => setModalBoxStatus('close'),
      });
    } else {
      confirmStatusChange(device._id, checked);
    }
  };

  /**
   * Renders the custom data for the availability column
   * @param {GuestChargingStationInterFace} device - The device data
   * @returns {JSX.Element} The rendered availability toggle cell
   */
  const renderAvailability = (device: GuestChargingStationInterFace) => (
    <div className="flex justify-center">
      <div key={device.id}>
        <SwitchButton
          checked={device.isActive}
          change={(checked: boolean) => handleAvailabilityChange(device, checked)}
        />
      </div>
    </div>
  );

  const column = useMemo(
    () => [
      {
        label: t('userCustomId'),
        accessorKey: 'userCustomId' as keyof GuestChargingStationInterFace,
        columnWidth: 200,

        /**
         * Side panel pages
         * @param {GuestChargingStationInterFace} consumer The consumer object
         * @returns {any} The consumer id button
         */
        customData: (consumer: GuestChargingStationInterFace) => (
          <button
            type="button"
            className="consumer-table-row"
            onClick={() => {
              setSelectedStation(consumer);
              setIsPanelOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                setSelectedStation(consumer);
                setIsPanelOpen(true);
              }
            }}>
            {consumer.userCustomId}
          </button>
        ),
      },
      {
        label: t('userName'),
        accessorKey: 'name' as keyof GuestChargingStationInterFace,
        sortAllow: true,
      },
      {
        label: t('phoneNumber'),
        accessorKey: 'phoneNumber' as keyof GuestChargingStationInterFace,
        /**
         * Date view
         * @param {GuestChargingStationInterFace} station The station object
         * @returns {any} The date element
         */
        customData: (station: GuestChargingStationInterFace) => (
          <div >
            +{station.countryCode} {station.phoneNumber || 'N/A'}
          </div>
        ),
      },
      {
        label: t('email'),
        accessorKey: 'email' as keyof GuestChargingStationInterFace,
      },
      {
        label: t('date'),
        accessorKey: 'fromDate' as keyof GuestChargingStationInterFace,
        sortAllow: true,
        /**
         * Date view
         * @param {GuestChargingStationInterFace} station The station object
         * @returns {any} The date element
         */
        customData: (station: GuestChargingStationInterFace) => (
          <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
            {ConvertUTCtoDate(station.fromDate)} - {ConvertUTCtoDate(station.toDate)}
          </div>
        ),
      },
      {
        label: t('time'),
        accessorKey: 'totalEnergyDispensed' as keyof GuestChargingStationInterFace,
        sortAllow: false,
        columnWidth: 160,
        /**
         * Date view
         * @param {GuestChargingStationInterFace} station The station object
         * @returns {any} The time element
         */
        customData: (station: GuestChargingStationInterFace) => (
          <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
            {FormatUtcString(ConvertUTCtoHHMM(station.createdAt))}
          </div>
        ),
      },
      {
        label: t('status'),
        accessorKey: 'uptimePercent' as keyof GuestChargingStationInterFace,
        customData: renderAvailability,
      },
      {
        label: t('duration'),
        accessorKey: 'duration' as keyof GuestChargingStationInterFace,
        sortAllow: true,
      },
    ],
    [t, renderAvailability],
  );

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('userCustomId'), accessorKey: 'userCustomId' },
    { label: t('userName'), accessorKey: 'name' },
    { label: t('phoneNumber'), accessorKey: 'phoneNumber' },
    { label: t('date'), accessorKey: 'fromDate' },
    { label: t('time'), accessorKey: 'totalEnergyDispensed' },
    { label: t('status'), accessorKey: 'uptimePercent' },
    { label: t('duration'), accessorKey: 'duration' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    stationManagementApi
      .fetchGuestChargingListExport({
        submissionStatus: 'approved',
        chargingStation: selectedChargingPoint?.id,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: GuestChargingStationInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'fromDate':
                return (
                  `${ConvertUTCtoDate(row?.createdAt)} - ${ConvertUTCtoDate(row?.toDate)}` || 'N/A'
                );
              case 'totalEnergyDispensed':
                return ConvertUTCtoHHMM(row?.createdAt) || 'N/A';
              case 'uptimePercent':
                return row?.isActive ? t('active') : t('inactive');
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-station-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-station-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param {any} value The pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  /**
   * Function for sorting table data
   * @param {any} sortData The sort data
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        ...queryParameters,
        page,
        limit,
      });
      dispatch(disableGuestChargingStationFiltersApplied());
    }
  }, [filters, selectedChargingPoint]);

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('GuestChargingStationFiltersList'); // string | null

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchListGuestChargingStationFilters({ chargingStation: currentId }));
    }
    if (GuestChargingStation.length === 0 || lastId !== currentId) {
      fetchData({
        page,
        limit,
      });
      dispatch(disableGuestChargingStationFiltersApplied());
    }
    // Save this id so next time we can compare
    sessionStorage.setItem('GuestChargingStationFiltersList', currentId);
  }, [selectedChargingPoint?.id, filters]);

  return (
    <div className="guest-charging-station-wrap" dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={modalBoxDetail.closeModal}
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
        rightCorner=""
      />
      <div className="guest-charging-left-content">
        <CustomTable
          version="v2"
          header={column}
          data={GuestChargingStation}
          serialNo
          sizeChangeButtonRequired
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filters={filters}
          filtersToDispatch={updateGuestChargingStationFilters}
          filterListToDispatch={fetchListGuestChargingStationFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          ReferenceCurrentPages={handleRefresh}
          pageLimitChange={pageNateLimitChange}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          onHiddenColumnsChange={setHiddenColumns}
        />
      </div>
      <div className="guest-charging-right-content">
        <p>{t('paidVsGuest')}</p>
        <div className="content-chart">
          <GuestChargingChart />
        </div>{' '}
      </div>{' '}
      {isPanelOpen && selectedStation && (
        <div className="side-panel-pages">
          <div className="close">
            <div>
              <MyButton
                label=""
                buttonType="none"
                svgIcon={<CloseIcon />}
                // disableHoverEffect={disableHover}
                onClickFunc={() => {
                  if (isPanelOpen) {
                    setIsPanelOpen(false);
                    setSelectedStation(null);
                  }
                }}
              />
            </div>
          </div>

          {/* Profile Section */}
          <div className="profile">
            {/* <img src={ProfileIcon} alt="profile" className="profile-image" /> */}
            <ProfileIcon />
            <div>
              <p className="name">{selectedStation.name}</p>
              <p className="gmail">{selectedStation.email}</p>
            </div>
          </div>

          {/* Details Section */}
          <div className="details">
            <div>
              <p className="label">Payment Method</p>
              <div className="detail-item">
                <p className="value">{selectedStation.paymentMethod}</p>
              </div>
            </div>
            <div>
              <p className="label">Phone Number</p>
              <div className="detail-item">
                <p className="value">+{selectedStation.countryCode} {selectedStation.phoneNumber}</p>
              </div>
            </div>
            <div>
              <div className="label-with-icon">
                <PaidAmountIcon style={{ color: '#00A79D' }} />
                <p className="label">Total Paid Amount</p>
              </div>
              <p className="value">{ToFixedValue(selectedStation?.transactionDetails?.totalPaidAmount || 0)}</p>
            </div>
            <div>
              <div className="label-with-icon">
                <p className="label">Total Power Consumed</p>
              </div>
              <p className="value">
                {ToFixedValue(selectedStation?.transactionDetails?.totalPowerConsumed || 0)}
              </p>
            </div>
          </div>

          {/* Session Section */}
          <div className="session-header">
            <ClockIcon style={{ color: '#00A79D' }} />
            <p className="session-title">Session Details</p>
          </div>
          <div className="session-details">
            <div className="session-container">
              <p className="label">Start Date & Time</p>
              <p className="value">
                {ConvertUTCtoDate(selectedStation.fromDate)}&{' '}
                {ConvertUTCtoHHMM(selectedStation.createdAt)}
              </p>
            </div>
            <div className="session-container">
              <p className="label">End Date & Time</p>
              <p className="value">
                {ConvertUTCtoDate(selectedStation.toDate)}&{' '}
                {ConvertUTCtoHHMM(selectedStation.createdAt)}
              </p>
            </div>
            <div className="session-container">
              <p className="label">Session Status</p>
              <p className="value status">-</p>
            </div>
            <div className="session-container">
              <p className="label">Power Consumed</p>
              <p className="value">-</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GuestCharging;
